import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { Trash2, X, Save, UserPlus, KeyRound, Copy, CheckCircle2, Shield, Crown, Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import PermissionsDrawer from "@/components/PermissionsDrawer";

// iter199 — Editable role vocabulary. Must match the backend
// `_ROLE_UPDATE_ALLOWED` set in server.py. `owner` is intentionally
// omitted — Owner is a FLAG (`is_owner`), not a selectable role.
const ROLE_OPTIONS = [
  { value: "admin",         label: "Admin" },
  { value: "manager",       label: "Manager" },
  { value: "coordinator",   label: "Coordinator" },
  { value: "viewer",        label: "Viewer" },
  { value: "editor",        label: "Editor" },
  { value: "photographer",  label: "Photographer" },
  { value: "cinematographer", label: "Cinematographer" },
  { value: "freelancer",    label: "Freelancer" },
  { value: "accountant",    label: "Accountant" },
  { value: "custom",        label: "Custom" },
];

export default function Users() {  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "coordinator" });
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch((e) => logger.debug("users list failed:", e?.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/users", form);
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "coordinator" });
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const [resetInfo, setResetInfo] = useState(null); // { email, reset_url, expires_at }
  const [resetErr, setResetErr] = useState("");
  // iter157 — Permissions drawer target
  const [permTargetId, setPermTargetId] = useState(null);
  // iter199 — Inline role edit state. `savingRoleId` holds the user_id
  // that's currently saving so we can render a spinner on that ONE row.
  const [savingRoleId, setSavingRoleId] = useState(null);
  const canManageRoles = user?.role === "admin";

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (u.is_owner) {
      toast.error("Owner accounts cannot have their role changed.");
      return;
    }
    if (user?.id === u.id && newRole !== "admin") {
      toast.error("You cannot demote your own account. Ask another admin.");
      return;
    }
    // Optimistic update — revert on error.
    const prevRole = u.role;
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
    setSavingRoleId(u.id);
    try {
      const { data } = await api.patch(`/auth/users/${u.id}/role`, { role: newRole });
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, ...data } : x));
      toast.success(`${u.name}'s role updated to ${newRole}`);
    } catch (e) {
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, role: prevRole } : x));
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingRoleId(null);
    }
  };

  const issueReset = async (u) => {
    setResetErr("");
    if (!window.confirm(`Generate a one-time password-reset link for ${u.email}?\n\nYou'll share the link with them via WhatsApp — it expires in 1 hour.`)) return;
    try {
      const { data } = await api.post("/auth/reset-tokens", { user_id: u.id });
      setResetInfo(data);
    } catch (e) {
      setResetErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Users</h1>
          <p className="text-stone-500 text-sm mt-1">Manage team access</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} data-testid="new-user-btn">
          <UserPlus className="w-4 h-4" strokeWidth={1.6} /> Invite user
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <div className="inline-flex items-center gap-1.5">
                    {/* iter199 — Owner rows keep the static badge (Owner
                        is a flag, not a role change). Non-admin viewers
                        keep the static badge too. Everyone else sees an
                        inline dropdown. */}
                    {(!canManageRoles || u.is_owner) ? (
                      <span
                        className={`badge ${u.role === "admin" ? "badge-red" : u.role === "manager" ? "badge-yellow" : u.role === "viewer" ? "badge-stone" : "badge-blue"}`}
                        data-testid={`user-role-badge-${u.id}`}
                      >
                        {u.role}
                      </span>
                    ) : (
                      <span className="relative inline-flex items-center">
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u, e.target.value)}
                          disabled={savingRoleId === u.id}
                          className={`appearance-none text-xs font-medium rounded-full px-2.5 py-0.5 pr-6 border cursor-pointer disabled:opacity-60 disabled:cursor-wait ${
                            u.role === "admin"
                              ? "bg-red-50 border-red-200 text-red-800"
                              : u.role === "manager"
                                ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                                : u.role === "viewer"
                                  ? "bg-stone-50 border-stone-200 text-stone-700"
                                  : "bg-blue-50 border-blue-200 text-blue-800"
                          }`}
                          data-testid={`user-role-select-${u.id}`}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-1.5 text-stone-500 text-[10px]">
                          {savingRoleId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "▼"}
                        </span>
                      </span>
                    )}
                    {u.is_owner && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-widest" data-testid={`user-owner-badge-${u.id}`}>
                        <Crown className="w-2.5 h-2.5" strokeWidth={2.5} /> Owner
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => setPermTargetId(u.id)}
                      className="inline-flex items-center gap-1 text-xs text-stone-700 hover:text-rose-800 hover:bg-rose-50 px-2 py-1 rounded-md"
                      title="Edit permissions"
                      data-testid={`user-permissions-${u.id}`}
                    >
                      <Shield className="w-3.5 h-3.5" strokeWidth={1.8} /> Permissions
                    </button>
                    <button
                      onClick={() => issueReset(u)}
                      className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900 hover:bg-rose-50 px-2 py-1 rounded-md"
                      title="Generate a one-time password reset link"
                      data-testid={`user-reset-${u.id}`}
                    >
                      <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} /> Reset link
                    </button>
                    {user?.id !== u.id && !u.is_owner && (
                      <button onClick={() => del(u.id)} className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-700 hover:bg-red-50" data-testid={`user-delete-${u.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {permTargetId && (
        <PermissionsDrawer
          userId={permTargetId}
          onClose={() => setPermTargetId(null)}
          onSaved={() => { /* audit updated; no list refresh needed */ }}
        />
      )}

      {resetErr && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error-banner">{resetErr}</div>
      )}

      {resetInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 fade-in px-4" onClick={() => setResetInfo(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="reset-link-modal">
            <div className="flex items-center justify-between px-5 h-14 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-rose-700" strokeWidth={1.8} />
                <h3 className="font-heading font-semibold">One-time reset link</h3>
              </div>
              <button className="btn-ghost p-2 rounded" onClick={() => setResetInfo(null)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-600">
                Share this link with <span className="font-semibold text-stone-800" data-testid="reset-modal-email">{resetInfo.email}</span> over WhatsApp.
                It expires in 1 hour and can be used exactly once.
              </p>
              <div className="border border-stone-200 rounded-lg bg-stone-50 p-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Reset URL</div>
                <div className="font-mono text-xs text-stone-800 break-all" data-testid="reset-modal-url">{resetInfo.reset_url}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <CopyBtn text={resetInfo.reset_url} label="Copy link" />
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Password reset link for Kahani Studio Manager (expires in 1 hour):\n\n${resetInfo.reset_url}`)}`}
                  target="_blank" rel="noreferrer"
                  className="btn btn-outline btn-sm"
                  data-testid="reset-wa-share"
                >
                  Share via WhatsApp
                </a>
              </div>
              <div className="text-[11px] text-stone-400">Expires at {new Date(resetInfo.expires_at).toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 fade-in" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-14 border-b border-stone-200">
              <h2 className="font-heading font-semibold">Invite team member</h2>
              <button className="btn-ghost p-2 rounded" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-3">
              <div>
                <label className="label">Name</label>
                <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
              </div>
              <div>
                <label className="label">Email</label>
                <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
              </div>
              <div>
                <label className="label">Password</label>
                <input required type="password" className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
              </div>
              <div>
                <label className="label">Role</label>
                <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-form-role">
                  <option value="coordinator">Coordinator — full CRUD on bookings & post-production</option>
                  <option value="manager">Manager — full ops access, financials hidden (iter120)</option>
                  <option value="viewer">Viewer — read-only access to Bookings only</option>
                  <option value="admin">Admin — full access incl. delete & user management</option>
                </select>
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="user-form-save"><Save className="w-4 h-4" /> Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CopyBtn({ text, label }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }
        catch { window.prompt("Copy the link:", text); }
      }}
      className="btn btn-primary btn-sm"
      data-testid="reset-copy-btn"
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />}
      {ok ? "Copied!" : label}
    </button>
  );
}
